<template>
  <div class="flex-1 pb-6 px-6 pt-3">
    <!-- Header Section -->
    <div class="bg-background border-b border-border px-8 py-4 -mx-6 mb-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold">Google Reviews</h1>
          <p class="text-muted-foreground text-sm">Manage and display Google reviews</p>
        </div>
        <div class="flex items-center space-x-3">
          <button class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 font-semibold">
            Create Customer
          </button>
        </div>
      </div>
    </div>
    <hr class="mb-6">

    <div v-if="user" class="px-2">
      <!-- Preview Card – changes with step -->
      <div 
        v-if="user.agency && user.agency.settings"
        class="rounded-lg border bg-card text-card-foreground shadow-sm mb-6"
        :style="`border-color: ${zensocialSettings.background_color} !important; background-color: ${zensocialSettings.background_color} !important;`"
      >
        <template v-if="Number(step) === 1">
          <div class="flex flex-col space-y-1.5 p-6">
            <h3 class="font-semibold tracking-tight text-center text-5xl" :style="`color: ${zensocialSettings.main_text_color} !important;`">{{ zensocialSettings.title || 'Google Reviews' }}</h3>
          </div>
          <div class="p-6 pt-0">
            <div class="flex gap-2 items-stretch">
              <div class="col double-column min-w-0" style="flex: 2 1 0%;">
                <input type="text" class="widget-embed-input flex h-12 w-full rounded-md border border-input px-4 py-3 text-lg bg-white text-foreground" placeholder="Start Typing To Find Your Business" />
              </div>
              <div class="col text-center center-content" style="flex: 1 1 0%;">
                <button class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium h-12 py-2 px-8 w-full" :style="`background-color: ${zensocialSettings.primary_button_background_color} !important; color: ${zensocialSettings.primary_button_text_color} !important; border: none;`">Submit</button>
              </div>
            </div>
          </div>
        </template>
        <template v-else-if="Number(step) === 2">
          <div class="flex flex-col space-y-1.5 p-6">
            <h3 class="font-semibold tracking-tight text-center text-5xl" :style="`color: ${zensocialSettings.main_text_color} !important;`">Would you like to add this to your website?</h3>
          </div>
          <div class="px-6 pb-6">
            <div class="flex flex-nowrap items-stretch gap-2">
              <input type="text" class="widget-embed-input flex h-11 flex-1 min-w-0 rounded-md border border-input px-3 py-2.5 text-base bg-white text-foreground" placeholder="First Name..." />
              <input type="text" class="widget-embed-input flex h-11 flex-1 min-w-0 rounded-md border border-input px-3 py-2.5 text-base bg-white text-foreground" placeholder="Email..." />
              <div class="flex-1 min-w-0 embed-step2-phone-wrapper">
                <VuePhoneNumberInput
                  v-model="previewPhone"
                  default-country-code="US"
                  valid-color="#e2e7f1"
                  no-example
                  :translations="embedStep2PhoneTranslations"
                  class="embed-step2-phone"
                />
              </div>
              <button type="button" class="widget-embed-input flex-1 min-w-0 h-11 inline-flex items-center justify-center whitespace-nowrap rounded-md text-base font-medium border-none" :style="`background-color: ${zensocialSettings.primary_button_background_color} !important; color: ${zensocialSettings.primary_button_text_color} !important;`" @click.prevent="advanceEmbedPreviewThankYouStep">I want this</button>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col p-8 text-center">
            <h3 class="font-semibold tracking-tight text-5xl mt-4 mb-4" :style="`color: ${zensocialSettings.main_text_color} !important;`">Thank You</h3>
            <p class="text-5xl italic mt-4 mb-4" :style="`color: ${zensocialSettings.main_text_color} !important; opacity: 0.95;`">Please check your email for more details.</p>
          </div>
        </template>
      </div>

      <!-- Widget Configuration Card -->
      <div class="rounded-lg border bg-card text-card-foreground shadow-sm">
        <div class="flex flex-col space-y-1.5 p-6">
          <h3 class="text-2xl font-semibold leading-none tracking-tight">Widget Configuration</h3>
        </div>
        <div class="p-6 pt-0 space-y-6">
          <ValidationObserver
            v-slot="{ handleSubmit, invalid, touched }"
            ref="embedForm"
          >
            <form v-if="user.agency.id && user.agency.settings.zensocial_embed_form_settings" class="settings-form" @submit.prevent="handleSubmit(updateEmbedForm)">
              <!-- Step and Title -->
              <div class="grid grid-cols-5 gap-4">
                <div class="space-y-2">
                  <label class="text-base font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Change Form Step</label>
                  <select v-model.number="step" class="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    <option :value="1">Step 1</option>
                    <option :value="2">Step 2</option>
                    <option :value="3">Step 3</option>
                  </select>
                </div>
                <div v-if="Number(step) === 1" class="col-span-4 space-y-2">
                  <label class="text-base font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Form Title</label>
                  <input 
                    v-model="user.agency.settings.zensocial_embed_form_settings.title" 
                    type="text"
                    class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  />
                </div>
              </div>

              <!-- Color Pickers -->
              <div class="grid grid-cols-4 gap-4">
                <div class="space-y-2">
                  <label class="text-base font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Main Background Color</label>
                  <input 
                    v-model="user.agency.settings.zensocial_embed_form_settings.background_color" 
                    type="color" 
                    class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10"
                  />
                </div>
                <div class="space-y-2">
                  <label class="text-base font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Main Text Color</label>
                  <input 
                    v-model="user.agency.settings.zensocial_embed_form_settings.main_text_color" 
                    type="color" 
                    class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10"
                  />
                </div>
                <div v-if="Number(step) !== 3" class="space-y-2">
                  <label class="text-base font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Primary Button Background Color</label>
                  <input 
                    v-model="user.agency.settings.zensocial_embed_form_settings.primary_button_background_color" 
                    type="color" 
                    class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10"
                  />
                </div>
                <div v-if="Number(step) !== 3" class="space-y-2">
                  <label class="text-base font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Primary Button Text Color</label>
                  <input 
                    v-model="user.agency.settings.zensocial_embed_form_settings.primary_button_text_color" 
                    type="color" 
                    class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm h-10"
                  />
                </div>
              </div>

              <!-- Embed Code Section -->
              <div class="space-y-2">
                <label class="text-base font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Copy and Paste this HTML where you want your Widget to display</label>
                <textarea 
                  :value="embedHTML.replaceAll('&lt;','<')" 
                  class="w-full min-h-[100px] rounded-md border border-input bg-white text-foreground px-3 py-2 text-base"
                  style="font-family: monospace;"
                ></textarea>
                <div class="flex gap-2">
                  <button 
                    type="button"
                    class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                    @click="copyEmbedCode"
                  >
                    Copy Code
                  </button>
                  <button 
                    type="submit"
                    class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 bg-foreground hover:bg-foreground/90 text-background"
                    :disabled="loadingUpdate || (invalid && touched)"
                  >
                    <b-spinner v-if="loadingUpdate" small />
                    <span v-else>Update</span>
                  </button>
                </div>
              </div>
            </form>
          </ValidationObserver>
        </div>
      </div>
    </div>
  </div>
</template>

  <script>
  import Vue from 'vue'
  import VuePhoneNumberInput from 'vue-phone-number-input'
  import 'vue-phone-number-input/dist/vue-phone-number-input.css'

  export default {
    components: {
      VuePhoneNumberInput,
    },
    data() {
      return {
        loadingUpdate: false,
        previewPhone: '',
        user: {
          agency: {
            whitelabel: {},
            settings:{}
          },
        },
        step:1,
        type: 'reviews'
      }
    },
    computed: {
    zensocialSettings() {
      const defaults = {
        title: 'Google Reviews',
        background_color: '#3a3a3a',
        main_text_color: '#ffffff',
        primary_button_background_color: '#2D9C41',
        primary_button_text_color: '#ffffff',
      }
      const settings = this.user?.agency?.settings?.zensocial_embed_form_settings || {}
      return { ...defaults, ...settings }
    },
    widgetLoaderScriptSrc() {
      const isStaging = typeof window !== 'undefined' && window.location.origin.includes('staging.')
      return isStaging
        ? 'https://rengine.sfo3.cdn.digitaloceanspaces.com/staging/generator-scripts/widget-generator-loader.js'
        : 'https://rengine.sfo3.cdn.digitaloceanspaces.com/generator-scripts/v4/widget-generator-loader.js'
    },
      embedFormSettings() {
        return this.user.agency.settings.zensocial_embed_form_settings || {
          title: 'Google Reviews',
          background_color: '#3a3a3a',
          main_text_color: '#ffffff',
          primary_button_background_color: '#2D9C41',
          primary_button_text_color: '#ffffff'
        }
      },
      embedHTML() {
        let widgetHTML = '';
        if (this.isRepuAgency) {
          widgetHTML = `<leads-widget-generator agency-id="${this.user.agency.id}" return-type="reviews" product="repuagency"></leads-widget-generator>`;
        } else if (this.isZenSocial) {
          widgetHTML = `<leads-widget-generator agency-id="${this.user.agency.id}" return-type="reviews" product="zensocial"></leads-widget-generator>`;
        } else {
          widgetHTML = `<leads-widget-generator agency-id="${this.user.agency.id}" return-type="reviews"></leads-widget-generator>`;
        }

        return `<!-- Start ZenSocial Generator -->
        &lt;script src="${this.widgetLoaderScriptSrc}">&lt;/script>
        ${widgetHTML}
<!-- End ZenSocial Generator -->`;
    },
    embedStep2PhoneTranslations() {
      return {
        countrySelectorLabel: ' ',
        countrySelectorError: ' ',
        phoneNumberLabel: ' ',
        example: ' ',
      };
    },
    },
    mounted() {
      this.user = Vue.util.extend({}, this.$store.getters['auth/user'])
      
      // Initialize zensocial_embed_form_settings if it doesn't exist
      if (!this.user.agency.settings.zensocial_embed_form_settings) {
        this.$set(this.user.agency.settings, 'zensocial_embed_form_settings', {
          title: 'Google Reviews',
          background_color: '#3a3a3a',
          main_text_color: '#ffffff',
          primary_button_background_color: '#2D9C41',
          primary_button_text_color: '#ffffff'
        })
      }
    },

    methods: {
      copyEmbedCode() {
        navigator.clipboard.writeText(this.embedHTML.replaceAll('&lt;','<'));
        Vue.$toast.open({
          message: 'Code Copied',
          type: 'success',
          duration: 5000,
        })
      },
      advanceEmbedPreviewThankYouStep() {
        if (Number(this.step) === 2) {
          this.step = 3;
        }
      },
      updateEmbedForm(){
        this.loadingUpdate = true

        // Ensure zensocial_embed_form_settings exists
        if (!this.user.agency.settings.zensocial_embed_form_settings) {
          this.$set(this.user.agency.settings, 'zensocial_embed_form_settings', {
            title: 'Google Reviews',
            background_color: '#3a3a3a',
            main_text_color: '#ffffff',
            primary_button_background_color: '#2D9C41',
            primary_button_text_color: '#ffffff'
          })
        }

        let params = {
          zensocial_embed_form_settings:this.user.agency.settings.zensocial_embed_form_settings,
          setting_form:'zensocial_embed_form'
        }

        this.$store
            .dispatch('agency/editSettings', params)
            .then(() => {
              this.loadingUpdate = false
            })
            .catch((err) => {
              if (err.response.data.errors) {
                this.$refs.embedForm.setErrors(err.response.data.errors)
              }
              this.loadingUpdate = false
            })
      },
    },
  }
  </script>
<style lang="scss" scoped>
// Most styles come from dashboard.scss utility classes
// Additional component-specific styles only

.settings-form {
  display: block;
}

// Ensure readonly textarea has proper styling
textarea[readonly] {
  background-color: #f9fafb;
  cursor: default;
}

// Step 2 phone input with country selector
.embed-step2-phone-wrapper {
  min-height: 2.75rem;
  display: flex;
  align-items: stretch;
  :deep(.vue-phone-number-input) {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: stretch;
    width: 100%;
    min-height: 2.75rem;
    gap: 0.25rem;
  }
  :deep(.select-country-container) {
    flex: 0 0 auto;
    max-width: 44%;
  }
  :deep(.vue-phone-number-input > .flex-1) {
    flex: 1 1 0%;
    min-width: 0;
  }
  :deep(.input-tel),
  :deep(.select-country-container) {
    height: 100%;
  }
  :deep(.input-tel__label),
  :deep(.country-selector__label),
  :deep(.m-input-label) {
    display: none !important;
  }
  :deep(.input-tel__input),
  :deep(.country-selector__input) {
    height: 2.75rem;
    border-radius: 7px;
    border: 0;
    padding: 8px;
    font-size: 1rem;
    line-height: 30px;
    box-sizing: border-box;
  }
}

// Grid column spans
.col-span-4 {
  grid-column: span 4 / span 4;
}

// Space utilities
.space-y-2 > * + * {
  margin-top: 0.5rem;
}

.space-y-6 > * + * {
  margin-top: 1.5rem;
}

// Leading utilities
.leading-none {
  line-height: 1;
}

.peer-disabled\:cursor-not-allowed:disabled ~ * {
  cursor: not-allowed;
}

.peer-disabled\:opacity-70:disabled ~ * {
  opacity: 0.7;
}

// Grid utilities
.grid-cols-5 {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.grid-cols-4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

// Min height for textarea
.min-h-\[100px\] {
  min-height: 100px;
}

// Width utilities
.w-full {
  width: 100%;
}
</style>
